import React from 'react';
import { SimpleProductType } from 'api/repositories';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { formatPrice } from 'common/functions';
import TextField from 'common/components/TextField';
import { SIZES } from 'views/pages/Product/common/constants';
import { makeProductsSetStyles } from 'styles/pages/productsSet';
import { Hidden } from '@material-ui/core';
import { Control, Controller } from 'react-hook-form';
import ErrorPanel from 'common/components/ErrorPanel';

type Props = {
    control: Control;
    errors: any;
    isSubmitted: boolean;
    productsSection: {
        maximumTitleQuantity: number;
        minimumSectionQuantity: number;
        minimumTitleQuantity: number;
        products: Array<SimpleProductType>;
        sectionName: string;
        sectionProductPrice: number;
    };
    sectionIndex: number;
    setPrice: Function;
};

const Set: React.FC<Props> = ({
    control,
    errors,
    isSubmitted,
    productsSection,
    sectionIndex,
    setPrice,
}) => {
    const {
        minimumSectionQuantity,
        maximumTitleQuantity,
        minimumTitleQuantity,
        products,
        sectionName,
        sectionProductPrice,
    } = productsSection;

    const {
        priceField,
        sectionTitle,
        tableHeader,
        tableHeaderItem,
        tableRow,
    } = makeProductsSetStyles();

    const { getValues } = control;

    return (
        <Box marginTop={4}>
            <Typography className={sectionTitle} variant="h6">
                {sectionName}
            </Typography>
            <Box>
                <Hidden xsDown>
                    <Box className={tableHeader}>
                        <Box className={tableHeaderItem} flex={0.6}>
                            tytuł książki
                        </Box>
                        <Box className={tableHeaderItem} flex={0.15}>
                            cena
                        </Box>
                        <Box className={tableHeaderItem} flex={0.25}>
                            liczba egz.
                        </Box>
                    </Box>
                </Hidden>
                <Controller
                    as={<input />}
                    control={control}
                    name={`sections.${sectionIndex}.price`}
                    type="hidden"
                    defaultValue={sectionProductPrice}
                />
                {products.map(({ code, images, name }, index) => {
                    const [{ absolutePath }] = images
                        .filter(({ type }) => type === 'thumbnails')[0]
                        .items[0].sizes.filter(({ size }) => size === SIZES.SMALL);

                    return (
                        <Box key={code} className={tableRow}>
                            <Controller
                                as={<input />}
                                control={control}
                                name={`sections.${sectionIndex}.items.${index}.productCode`}
                                type="hidden"
                                defaultValue={code}
                            />
                            <Box flex={0.6} justifyContent="flex-start">
                                <Box display="flex" marginRight={2}>
                                    <img alt="" src={absolutePath} />
                                </Box>
                                <Box>{name}</Box>
                            </Box>
                            <Box flex={0.15} fontSize="1rem" justifyContent="center">
                                <b>{formatPrice(sectionProductPrice)}</b>
                            </Box>
                            <Box flex={0.25} flexDirection="column" justifyContent="center">
                                <Controller
                                    as={<TextField className={priceField} fullWidth />}
                                    onChange={([event]) => {
                                        const value = event.target.value;
                                        const newValue = value.replace(/[^\d]/, '');

                                        setPrice(`sections.${sectionIndex}.minimumTitleQuantity`);

                                        return newValue === '' ? newValue : Number(newValue);
                                    }}
                                    label="egz."
                                    type="text"
                                    variant="outlined"
                                    name={`sections.${sectionIndex}.items.${index}.quantity`}
                                    control={control}
                                    defaultValue={0}
                                    rules={{
                                        validate: (value) =>
                                            Number(value) !== 0 && value < minimumSectionQuantity
                                                ? `Min. liczba egzemplarzy: ${minimumSectionQuantity}`
                                                : undefined,
                                    }}
                                />
                                <ErrorPanel
                                    errors={errors}
                                    name={`sections.${sectionIndex}.items.${index}.quantity`}
                                />
                            </Box>
                        </Box>
                    );
                })}
                <Controller
                    as={<input />}
                    control={control}
                    name={`sections.${sectionIndex}.minimumTitleQuantity`}
                    type="hidden"
                    defaultValue={minimumTitleQuantity}
                    rules={{
                        validate: () => {
                            const values = getValues({ nest: true });
                            const sectionTitlesQuantity = values.sections[
                                sectionIndex
                            ].items.filter((title: any) => title.quantity > 0).length;

                            if (sectionTitlesQuantity < minimumTitleQuantity) {
                                return `Min. liczba tytułów w zestawie: ${minimumTitleQuantity}`;
                            }

                            if (maximumTitleQuantity === 0) return;

                            return sectionTitlesQuantity > maximumTitleQuantity
                                ? `Max. liczba tytułów w zestawie: ${maximumTitleQuantity}`
                                : undefined;
                        },
                    }}
                />
                {isSubmitted && (
                    <ErrorPanel
                        errors={errors}
                        name={`sections.${sectionIndex}.minimumTitleQuantity`}
                    />
                )}
            </Box>
        </Box>
    );
};

export default Set;
